if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.cartUpdateUnsubscriber = undefined;
        
        // Track current button state to prevent redundant updates
        this.currentButtonState = {
          isPreorder: null,
          variantId: null,
          buttonText: null
        };
        
        // Debounce timer for variant changes
        this.variantChangeDebounceTimer = null;
        this.isInitialLoad = true;

        // Track sold-out popup visibility globally to avoid duplicate triggers
        if (!window.__klaviyoSoldOutState) {
          window.__klaviyoSoldOutState = { openedVariantIds: {}, pendingVariantIds: {}, openedProductIds: {}, pendingProductIds: {} };
        }
      }

      connectedCallback() {
        // Subscribe to cart updates for products with default OTP
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.handleCartUpdate(event);
          // Don't call handleVariantChangeForPreorder here - handleCartUpdate handles it
        });

        // Subscribe to variant changes for pre-order updates (debounced)
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          // Debounce variant changes to prevent excessive updates
          if (this.variantChangeDebounceTimer) {
            clearTimeout(this.variantChangeDebounceTimer);
          }
          this.variantChangeDebounceTimer = setTimeout(() => {
            this.handleVariantChangeForPreorder(event);
          }, 100);
        });

        // Show loading state while checking initial button state
        this.setInitialLoadingState();

        // Check initial state on load
        const productInfo = this.closest('product-info');
        const initialChecks = [];
        
        if (productInfo && productInfo.dataset.defaultOtp === 'true') {
          initialChecks.push(this.updateBuyButtonForOTP());
        }

        // Always check initial cart state for pre-order status
        initialChecks.push(this.checkInitialCartStateForPreorder());

        // Remove loading state once all checks complete
        Promise.allSettled(initialChecks).then(() => {
          this.removeInitialLoadingState();
          this.isInitialLoad = false;
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        // Clear debounce timer
        if (this.variantChangeDebounceTimer) {
          clearTimeout(this.variantChangeDebounceTimer);
        }
      }

      /**
       * Handle cart update events for OTP products and preorder inventory checks
       */
      async handleCartUpdate(event) {
        const productInfo = this.closest('product-info');
        
        // Handle OTP products
        if (productInfo && productInfo.dataset.defaultOtp === 'true') {
          this.updateBuyButtonForOTP();
        }

        // Check variant inventory after cart update for preorder logic
        // Only check if we have cart data and variant ID, and it's not initial load
        if (event.cartData && event.productVariantId && !this.isInitialLoad) {
          await this.checkVariantInventoryAfterCartUpdate(event.productVariantId, event.cartData);
        }
      }

      /**
       * Set loading state for initial button check
       */
      setInitialLoadingState() {
        // Only show loading if button is not already in a loading state (e.g., from form submission)
        if (this.submitButton.classList.contains('loading')) {
          return;
        }

        const loadingSpinner = this.querySelector('.loading__spinner');
        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }
        this.submitButton.classList.add('loading');
        // Store original text if not already stored
        if (!this.originalButtonText) {
          this.originalButtonText = this.submitButtonText.textContent;
        }
      }

      /**
       * Remove loading state after initial checks complete
       */
      removeInitialLoadingState() {
        // Only remove loading if we're not in the middle of a form submission
        // Check if button is disabled due to form submission
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        const loadingSpinner = this.querySelector('.loading__spinner');
        if (loadingSpinner) {
          loadingSpinner.classList.add('hidden');
        }
        this.submitButton.classList.remove('loading');
      }

      /**
       * Check initial cart state on page load using Liquid's pre-calculated state
       * Liquid already calculated pre-order status based on cart quantities, so we use that
       * Stores state in window for persistence
       */
      async checkInitialCartStateForPreorder() {
        try {
          const variantId = this.variantIdInput?.value;
          if (!variantId) {
            return;
          }

          // Initialize window-level state tracker if it doesn't exist
          if (!window.productPreorderState) {
            window.productPreorderState = {};
          }

          // Use Liquid's pre-calculated state from data attributes (source of truth)
          const liquidIsPreorder = this.dataset.isPreorder === 'true';
          const liquidVariantId = this.dataset.variantId;
          const liquidCartQuantity = parseInt(this.dataset.cartVariantQuantity || '0');
          
          // Only proceed if this is the same variant that Liquid calculated for
          if (liquidVariantId === variantId) {
            const variantData = this.getSelectedVariantData();
            if (variantData) {
              console.log('[Pre-order] Using Liquid-calculated state:', {
                variantId: variantId,
                liquidIsPreorder: liquidIsPreorder,
                liquidCartQuantity: liquidCartQuantity,
                inventoryQuantity: variantData.inventory_quantity
              });
              
              // Store Liquid's state in window for persistence
              window.productPreorderState[variantId] = {
                isPreorder: liquidIsPreorder,
                inventoryQuantity: variantData.inventory_quantity,
                cartQuantity: liquidCartQuantity,
                inventoryPolicy: variantData.inventory_policy,
                lastUpdated: Date.now()
              };
              
              // Use Liquid's state as source of truth
              this.updatePreorderState(liquidIsPreorder, variantData);
              
              // Update tracked state
              this.currentButtonState = {
                isPreorder: liquidIsPreorder,
                variantId: variantId,
                buttonText: liquidIsPreorder ? (this.dataset.preorderButtonText || 'Pre-order now') : (window.variantStrings?.addToCart || 'Add to cart')
              };
              
              return; // Exit early, Liquid state is correct
            }
          }

          // Fallback: if Liquid data not available, calculate manually
          // This should rarely happen, but provides a safety net
          let cartQuantity = 0;
          try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            const cartItem = cart.items?.find(item => item.variant_id === parseInt(variantId));
            cartQuantity = cartItem?.quantity || 0;
          } catch (error) {
            console.error('[Pre-order] Error fetching cart:', error);
          }

          const variantData = this.getSelectedVariantData();
          if (!variantData) {
            return;
          }

          // Use same logic as Liquid
          const isPreorder = this.calculatePreorderStatus(variantData, cartQuantity);
          
          // Store in window state
          window.productPreorderState[variantId] = {
            isPreorder: isPreorder,
            inventoryQuantity: variantData.inventory_quantity,
            cartQuantity: cartQuantity,
            inventoryPolicy: variantData.inventory_policy,
            lastUpdated: Date.now()
          };
          
          this.updatePreorderState(isPreorder, variantData);
          this.currentButtonState = {
            isPreorder: isPreorder,
            variantId: variantId,
            buttonText: isPreorder ? (this.dataset.preorderButtonText || 'Pre-order now') : (window.variantStrings?.addToCart || 'Add to cart')
          };
        } catch (error) {
          console.error('[Pre-order] Error checking initial cart state:', error);
        }
      }

      /**
       * Calculate pre-order status using same logic as Liquid
       * This ensures consistency between Liquid and JavaScript
       * Can use window-level stored state if available
       */
      calculatePreorderStatus(variantData, cartQuantity) {
        const variantId = variantData.id?.toString();
        
        // Check window-level stored state first (if available and recent)
        if (window.productPreorderState && variantId && window.productPreorderState[variantId]) {
          const storedState = window.productPreorderState[variantId];
          const stateAge = Date.now() - storedState.lastUpdated;
          
          // Use stored state if it's less than 5 seconds old and cart quantity matches
          if (stateAge < 5000 && storedState.cartQuantity === cartQuantity) {
            console.log('[Pre-order] Using stored state:', {
              variantId: variantId,
              storedIsPreorder: storedState.isPreorder,
              cartQuantity: cartQuantity
            });
            return storedState.isPreorder;
          }
        }
        
        // Calculate fresh if no stored state or state is stale
        if (variantData.inventory_policy !== 'continue') {
          return false;
        }

        // Pre-order if variant is out of stock
        if (variantData.inventory_quantity <= 0) {
          return true;
        }

        // Pre-order if all available stock is in cart
        if (variantData.inventory_management === 'shopify' &&
            variantData.inventory_quantity <= cartQuantity &&
            cartQuantity > 0) {
          return true;
        }

        return false;
      }

      /**
       * Handle variant changes to update pre-order state
       * Optimized: Skips checks if inventory > 5
       */
      async handleVariantChangeForPreorder(event) {
        // Get variant ID from form or event
        const variantId = this.variantIdInput?.value || event?.variantId;
        if (!variantId) {
          return;
        }

        // Skip if this is the same variant and we're still on initial load
        if (this.isInitialLoad && this.currentButtonState.variantId === variantId) {
          return;
        }

        // Fast check: Skip if inventory > 5 and Liquid says not pre-order
        // Note: This check uses current variant's data attributes
        const inventoryQuantity = parseInt(this.dataset.variantInventoryQuantity || '0');
        const isPreorder = this.dataset.isPreorder === 'true';
        
        if (inventoryQuantity > 5 && !isPreorder) {
          console.log('[Pre-order] Skipping variant change check - sufficient inventory (>5)');
          // Still update button to "Add to cart" if needed
          const variantData = this.getSelectedVariantData();
          if (variantData && this.currentButtonState.isPreorder !== false) {
            this.updatePreorderState(false, variantData);
            this.currentButtonState = {
              isPreorder: false,
              variantId: variantId,
              buttonText: window.variantStrings?.addToCart || 'Add to cart'
            };
          }
          return;
        }

        // Get variant data from embedded JSON (no fetch needed)
        const variantData = this.getSelectedVariantData();
        if (!variantData) {
          return;
        }

        // Get cart quantity from Liquid data or fetch if needed
        let cartQuantity = parseInt(this.dataset.cartVariantQuantity || '0');
        if (cartQuantity === 0) {
          try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            const cartItem = cart.items?.find(item => item.variant_id === parseInt(variantId));
            cartQuantity = cartItem?.quantity || 0;
          } catch (error) {
            console.error('[Pre-order] Error fetching cart:', error);
          }
        }

        // Use same calculation logic as Liquid
        const shouldBePreorder = this.calculatePreorderStatus(variantData, cartQuantity);

        // Only update if state actually changed
        if (this.currentButtonState.variantId !== variantId || 
            this.currentButtonState.isPreorder !== shouldBePreorder) {
          
          console.log('[Pre-order] Variant change with cart check:', {
            variantId: variantData.id,
            inventoryQuantity: variantData.inventory_quantity,
            cartQuantity: cartQuantity,
            shouldBePreorder: shouldBePreorder
          });

          // Update button state
          this.updatePreorderState(shouldBePreorder, variantData);
          
          // Update tracked state
          this.currentButtonState = {
            isPreorder: shouldBePreorder,
            variantId: variantId,
            buttonText: shouldBePreorder ? (this.dataset.preorderButtonText || 'Pre-order now') : (window.variantStrings?.addToCart || 'Add to cart')
          };
        }
      }

      /**
       * Get selected variant data from DOM
       * First tries to get from product JSON (all products), then falls back to data-selected-variant
       */
      getSelectedVariantData() {
        try {
          const productInfo = this.closest('product-info');
          if (!productInfo) return null;

          const variantId = this.variantIdInput?.value;
          if (!variantId) return null;

          // Try to get from product JSON (available for all products)
          const productJsonElement = productInfo.querySelector('[data-product-json]');
          if (productJsonElement) {
            const productData = JSON.parse(productJsonElement.textContent);
            const variant = productData.variants?.find(v => v.id === parseInt(variantId));
            if (variant) {
              return variant;
            }
          }

          // Fallback to data-selected-variant (only available for products with variant picker)
          const variantDataElement = productInfo.querySelector('[data-selected-variant]');
          if (variantDataElement) {
            return JSON.parse(variantDataElement.textContent);
          }

          return null;
        } catch (error) {
          console.error('[Pre-order] Error parsing variant data:', error);
          return null;
        }
      }

      /**
       * Get variant inventory data, using embedded JSON first, only fetching if needed
       * Optimized to avoid unnecessary API calls when stock is sufficient
       * 
       * @param {string|number} variantId - The variant ID to check
       * @param {number} cartQuantity - Current quantity in cart for this variant
       * @returns {Promise<Object|null>} Variant data object or null
       */
      async fetchVariantInventory(variantId, cartQuantity = 0) {
        try {
          // First, try to get variant data from embedded JSON (fast, no fetch)
          const embeddedVariantData = this.getSelectedVariantData();
          
          if (embeddedVariantData) {
            const inventoryQty = embeddedVariantData.inventory_quantity || 0;
            const inventoryManagement = embeddedVariantData.inventory_management;
            const inventoryPolicy = embeddedVariantData.inventory_policy;
            
            // Determine if we need to fetch fresh data:
            // 1. Stock is insufficient (< 5) or unavailable
            // 2. Stock might be depleted (inventory_quantity <= cartQuantity)
            // 3. Variant is out of stock but continues selling (needs verification)
            const needsFetch = 
              (inventoryManagement === 'shopify' && inventoryQty < 5) ||
              (inventoryManagement === 'shopify' && inventoryQty <= cartQuantity) ||
              (inventoryQty <= 0 && inventoryPolicy === 'continue');
            
            // If stock is sufficient (>= 5) and not depleted, use embedded data
            if (!needsFetch && inventoryQty >= 5 && inventoryQty > cartQuantity) {
              console.log('[Pre-order] Using embedded JSON data (sufficient stock):', {
                variantId: embeddedVariantData.id,
                inventoryQty: inventoryQty,
                cartQuantity: cartQuantity
              });
              return embeddedVariantData;
            }
            
            // If we need fresh data, fetch from API
            console.log('[Pre-order] Fetching fresh inventory data (low stock or depleted):', {
              variantId: embeddedVariantData.id,
              inventoryQty: inventoryQty,
              cartQuantity: cartQuantity,
              needsFetch: needsFetch
            });
          }

          // Fetch fresh data from API
          const productInfo = this.closest('product-info');
          if (!productInfo) return embeddedVariantData || null;

          const productUrl = productInfo.dataset.url || window.location.pathname;
          const sectionId = productInfo.dataset.section || this.dataset.sectionId || 'main-product';
          
          // Fetch product data with variant information
          const response = await fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`);
          if (!response.ok) return embeddedVariantData || null;

          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Try to get variant from product JSON (available for all products)
          const productJsonElement = doc.querySelector('[data-product-json]');
          if (productJsonElement) {
            const productData = JSON.parse(productJsonElement.textContent);
            const variant = productData.variants?.find(v => v.id === parseInt(variantId));
            if (variant) {
              return variant;
            }
          }

          // Fallback to data-selected-variant (only available for products with variant picker)
          const variantDataElement = doc.querySelector('[data-selected-variant]');
          if (variantDataElement) {
            return JSON.parse(variantDataElement.textContent);
          }

          // Return embedded data as fallback if fetch failed
          return embeddedVariantData || null;
        } catch (error) {
          console.error('[Pre-order] Error fetching variant inventory:', error);
          // Return embedded data as fallback
          return this.getSelectedVariantData();
        }
      }

      /**
       * Check variant inventory after cart update
       * Optimized: Skips all checks if inventory > 5 (plenty of stock)
       * Uses Liquid's pre-calculated state when possible
       */
      async checkVariantInventoryAfterCartUpdate(variantId, cartData) {
        try {
          const variantIdStr = variantId.toString();
          
          // Fast check: Skip if inventory > 5 and Liquid says not pre-order
          if (this.shouldSkipPreorderCheck()) {
            console.log('[Pre-order] Skipping cart update check - sufficient inventory (>5)');
            return;
          }

          // Get variant data from embedded JSON (no fetch needed)
          const variantData = this.getSelectedVariantData();
          if (!variantData) return;

          // Get cart quantity - try Liquid data first, then fetch if needed
          let cartQuantity = parseInt(this.dataset.cartVariantQuantity || '0');
          
          // Only fetch cart if we don't have Liquid data or need fresh data
          if (cartQuantity === 0) {
            try {
              const cartResponse = await fetch('/cart.js');
              const cart = await cartResponse.json();
              const cartItem = cart.items?.find(item => item.variant_id === parseInt(variantId));
              cartQuantity = cartItem?.quantity || 0;
            } catch (error) {
              console.error('[Pre-order] Error fetching cart after update:', error);
              // Fallback to event data if fetch fails
              const cartItem = cartData?.items?.find(item => item.variant_id === parseInt(variantId));
              cartQuantity = cartItem?.quantity || 0;
            }
          }

          // Use same calculation logic as Liquid
          const shouldBePreorder = this.calculatePreorderStatus(variantData, cartQuantity);

          // Initialize window-level state tracker if it doesn't exist
          if (!window.productPreorderState) {
            window.productPreorderState = {};
          }

          // Store state in window for persistence
          window.productPreorderState[variantIdStr] = {
            isPreorder: shouldBePreorder,
            inventoryQuantity: variantData.inventory_quantity,
            cartQuantity: cartQuantity,
            inventoryPolicy: variantData.inventory_policy,
            lastUpdated: Date.now()
          };

          // Only update if state actually changed
          if (this.currentButtonState.variantId !== variantIdStr || 
              this.currentButtonState.isPreorder !== shouldBePreorder) {
            
            console.log('[Pre-order] Cart update inventory check:', {
              variantId: variantData.id,
              inventoryQuantity: variantData.inventory_quantity,
              cartQuantity: cartQuantity,
              inventoryPolicy: variantData.inventory_policy,
              shouldBePreorder: shouldBePreorder
            });

            // Update preorder state
            this.updatePreorderState(shouldBePreorder, variantData);
            
            // Update tracked state
            this.currentButtonState = {
              isPreorder: shouldBePreorder,
              variantId: variantIdStr,
              buttonText: shouldBePreorder ? (this.dataset.preorderButtonText || 'Pre-order now') : (window.variantStrings?.addToCart || 'Add to cart')
            };
          }
        } catch (error) {
          console.error('[Pre-order] Error checking inventory after cart update:', error);
        }
      }

      /**
       * Update preorder state from variant data
       */
      updatePreorderStateFromVariantData(variantData) {
        // Check if variant qualifies for pre-order:
        // inventory_quantity <= 0 AND inventory_policy == 'continue'
        const isPreorder = variantData.inventory_quantity <= 0 && 
                          variantData.inventory_policy === 'continue';

        console.log('[Pre-order] Variant change detected:', {
          variantId: variantData.id,
          inventoryQuantity: variantData.inventory_quantity,
          inventoryPolicy: variantData.inventory_policy,
          available: variantData.available,
          isPreorder: isPreorder
        });

        this.updatePreorderState(isPreorder, variantData);
      }

      /**
       * Check if we should skip pre-order checks (inventory > 5 means plenty of stock)
       * Uses Liquid's pre-calculated data attributes for fast check
       */
      shouldSkipPreorderCheck() {
        const inventoryQuantity = parseInt(this.dataset.variantInventoryQuantity || '0');
        const isPreorder = this.dataset.isPreorder === 'true';
        
        // Skip checks if inventory > 5 AND Liquid says it's not pre-order
        // Only check if inventory <= 5 OR Liquid says it's pre-order
        if (inventoryQuantity > 5 && !isPreorder) {
          return true;
        }
        return false;
      }

      /**
       * Check variant inventory before form submission and set preorder attributes if needed
       * Optimized: Skips all checks if inventory > 5 (plenty of stock)
       */
      async checkAndSetPreorderAttributes() {
        const variantId = this.variantIdInput?.value;
        if (!variantId) return;

        // Fast check: Skip if inventory > 5 and Liquid says not pre-order
        if (this.shouldSkipPreorderCheck()) {
          console.log('[Pre-order] Skipping check - sufficient inventory (>5)');
          return;
        }

        // Get quantity from form
        const quantityInput = this.form.querySelector('input[name="quantity"]');
        const requestedQuantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

        // Get variant data from embedded JSON (no fetch needed)
        const variantData = this.getSelectedVariantData();
        if (!variantData) return;

        // Get cart quantity from embedded JSON or Liquid data
        const liquidCartQuantity = parseInt(this.dataset.cartVariantQuantity || '0');
        let currentCartQuantity = liquidCartQuantity;

        // Only fetch cart if we don't have Liquid data
        if (liquidCartQuantity === 0) {
          try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            const cartItem = cart.items?.find(item => item.variant_id === parseInt(variantId));
            currentCartQuantity = cartItem?.quantity || 0;
          } catch (error) {
            console.error('[Pre-order] Error fetching cart:', error);
          }
        }

        this.checkPreorderFromVariantData(variantData, requestedQuantity, currentCartQuantity);
      }

      /**
       * Check preorder status from variant data and set attributes accordingly
       */
      checkPreorderFromVariantData(variantData, requestedQuantity, currentCartQuantity) {
        // Calculate total quantity that will be in cart after this addition
        const totalQuantityAfterAdd = currentCartQuantity + requestedQuantity;

        // Check if inventory is insufficient:
        // - Variant uses Shopify inventory management
        // - Available inventory is less than what will be in cart
        // - Inventory policy allows continuing to sell
        const isInsufficientInventory = variantData.inventory_management === 'shopify' &&
                                        variantData.inventory_quantity < totalQuantityAfterAdd &&
                                        variantData.inventory_policy === 'continue';

        // Check if variant is fully out of stock but continues selling
        const isOutOfStockPreorder = variantData.inventory_quantity <= 0 &&
                                     variantData.inventory_policy === 'continue';

        const shouldMarkAsPreorder = isOutOfStockPreorder || isInsufficientInventory;

        console.log('[Pre-order] Pre-submission check:', {
          variantId: variantData.id,
          inventoryQuantity: variantData.inventory_quantity,
          requestedQuantity: requestedQuantity,
          currentCartQuantity: currentCartQuantity,
          totalQuantityAfterAdd: totalQuantityAfterAdd,
          isInsufficientInventory: isInsufficientInventory,
          isOutOfStockPreorder: isOutOfStockPreorder,
          shouldMarkAsPreorder: shouldMarkAsPreorder
        });

        // Update preorder property in form
        let preorderProperty = this.form.querySelector('.preorder-property');
        
        if (shouldMarkAsPreorder) {
          // Add pre-order property if it doesn't exist
          if (!preorderProperty) {
            preorderProperty = document.createElement('input');
            preorderProperty.type = 'hidden';
            preorderProperty.name = 'properties[Availability]';
            preorderProperty.value = 'Pre-order';
            preorderProperty.className = 'preorder-property';
            this.form.appendChild(preorderProperty);
          }
        } else {
          // Remove pre-order property if inventory is sufficient
          if (preorderProperty) {
            preorderProperty.remove();
          }
        }
      }

      /**
       * Update pre-order state (button text, notice, and properties)
       */
      updatePreorderState(isPreorder, variant) {
        const sectionId = this.dataset.sectionId;
        const preorderNotice = document.getElementById(`preorder-notice-${sectionId}`);
        
        // Update pre-order notice visibility (only if notice exists in DOM)
        if (preorderNotice) {
          if (isPreorder) {
            preorderNotice.style.display = '';
            preorderNotice.removeAttribute('hidden');
          } else {
            preorderNotice.style.display = 'none';
            preorderNotice.setAttribute('hidden', '');
          }
        }

        // Update hidden property input for cart line item
        let preorderProperty = this.form.querySelector('.preorder-property');
        
        if (isPreorder) {
          // Add pre-order property if it doesn't exist
          if (!preorderProperty) {
            preorderProperty = document.createElement('input');
            preorderProperty.type = 'hidden';
            preorderProperty.name = 'properties[Availability]';
            preorderProperty.value = 'Pre-order';
            preorderProperty.className = 'preorder-property';
            this.form.appendChild(preorderProperty);
          }
        } else {
          // Remove pre-order property if variant is in stock
          if (preorderProperty) {
            preorderProperty.remove();
          }
        }

        // Update button text based on pre-order status
        this.updateButtonTextForPreorder(isPreorder, variant);
      }

      /**
       * Get current pre-order state for a variant from window storage
       * Returns null if no state is stored
       */
      getPreorderStateFromStorage(variantId) {
        if (!window.productPreorderState || !variantId) {
          return null;
        }
        const variantIdStr = variantId.toString();
        return window.productPreorderState[variantIdStr] || null;
      }

      /**
       * Update button text for pre-order vs regular availability
       * Includes protection against incorrect state resets
       */
      updateButtonTextForPreorder(isPreorder, variant) {
        const variantId = variant?.id?.toString() || this.variantIdInput?.value;
        
        // CRITICAL: Check stored state - if we're trying to set isPreorder to false
        // but stored state says true for the same variant, preserve pre-order state
        if (!isPreorder && variantId) {
          const storedState = this.getPreorderStateFromStorage(variantId);
          if (storedState && storedState.isPreorder === true) {
            // Verify this is still correct by checking current inventory
            const variantData = this.getSelectedVariantData();
            if (variantData && variantData.inventory_quantity <= 0 && variantData.inventory_policy === 'continue') {
              console.log('[Pre-order] Preventing incorrect reset - preserving pre-order state:', {
                variantId: variantId,
                attemptedIsPreorder: isPreorder,
                storedIsPreorder: storedState.isPreorder,
                inventoryQuantity: variantData.inventory_quantity
              });
              // Keep pre-order state
              isPreorder = true;
            }
          }
        }
        
        const isUnavailable = !variant.available && !isPreorder;
        this.maybeOpenSoldOutKlaviyoPopup(isUnavailable, variantId);
        
        // Check if button is disabled due to collection restrictions
        // Check the button text span content, not the full button (which may include spinner)
        const isCollectionRestricted = this.submitButtonText.textContent.trim() === 'Already in cart' ||
                                      this.submitButton.hasAttribute('disabled') && 
                                      this.submitButton.classList.contains('disabled');
        
        if (isCollectionRestricted) {
          return; // Don't override collection restriction text
        }

        let buttonText = '';
        
        if (isPreorder) {
          // Use custom pre-order button text from metafield or default
          buttonText = this.dataset.preorderButtonText || 'Pre-order now';
          this.submitButton.removeAttribute('disabled');
          this.submitButton.classList.remove('disabled');
        } else if (isUnavailable) {
          buttonText = window.variantStrings?.soldOut || 'Sold out';
          this.submitButton.setAttribute('disabled', 'disabled');
        } else {
          buttonText = window.variantStrings?.addToCart || 'Add to cart';
          this.submitButton.removeAttribute('disabled');
          this.submitButton.classList.remove('disabled');
        }

        // Only update if text actually changed
        if (this.submitButtonText.textContent !== buttonText) {
          console.log('[Pre-order] Button updated:', {
            isPreorder,
            isUnavailable,
            buttonText,
            variantId: variantId,
            disabled: this.submitButton.hasAttribute('disabled')
          });

          this.submitButtonText.textContent = buttonText;
          
          // Update tracked state
          if (variantId) {
            this.currentButtonState = {
              isPreorder: isPreorder,
              variantId: variantId,
              buttonText: buttonText
            };
          }
        }
      }

      /**
       * Open Klaviyo sold-out form only for truly unavailable variants.
       * Replaces "PRODUCT" with current product title and "category" with
       * the last breadcrumb label.
       */
      async maybeOpenSoldOutKlaviyoPopup(isUnavailable, variantId) {
        if (!isUnavailable || !variantId) return;
        const soldOutState = window.__klaviyoSoldOutState || { openedVariantIds: {}, pendingVariantIds: {}, openedProductIds: {}, pendingProductIds: {} };
        window.__klaviyoSoldOutState = soldOutState;
        const productId = this.getCurrentProductId();

        const openedVariantIds = soldOutState.openedVariantIds || {};
        const pendingVariantIds = soldOutState.pendingVariantIds || {};
        const openedProductIds = soldOutState.openedProductIds || {};
        const pendingProductIds = soldOutState.pendingProductIds || {};

        if (openedVariantIds[variantId]) return;
        if (pendingVariantIds[variantId]) return;
        if (productId && openedProductIds[productId]) return;
        if (productId && pendingProductIds[productId]) return;

        if (!this.shouldShowSoldOutPopupForProduct(productId)) {
          console.debug('[Klaviyo Sold Out] Daily product throttle active', { productId, variantId });
          return;
        }

        pendingVariantIds[variantId] = true;
        if (productId) {
          pendingProductIds[productId] = true;
        }
        soldOutState.pendingVariantIds = pendingVariantIds;
        soldOutState.pendingProductIds = pendingProductIds;

        openedVariantIds[variantId] = true;
        delete pendingVariantIds[variantId];
        if (productId) {
          openedProductIds[productId] = true;
          delete pendingProductIds[productId];
        }
        soldOutState.openedVariantIds = openedVariantIds;
        soldOutState.pendingVariantIds = pendingVariantIds;
        soldOutState.openedProductIds = openedProductIds;
        soldOutState.pendingProductIds = pendingProductIds;

        this.markSoldOutPopupSeen(productId);

        this.openKlaviyoFormAndUpdateCopy();
      }

      openKlaviyoFormAndUpdateCopy() {
        const KLAVIYO_FORM_ID = this.getKlaviyoSoldOutFormId();
        if (!KLAVIYO_FORM_ID) return;
        const klaviyoQueue = (window._klOnsite = window._klOnsite || []);
        klaviyoQueue.push(['openForm', KLAVIYO_FORM_ID]);
        console.log('klaviyoQueue', klaviyoQueue, KLAVIYO_FORM_ID);
        const productTitle = this.getCurrentProductTitle();
        const breadcrumbCategory = this.getLastBreadcrumbLabel();
        const categoryLabel = breadcrumbCategory || 'this category';

        this.waitForKlaviyoForm(KLAVIYO_FORM_ID, (formElement) => {
          if (!formElement) return;
          this.replaceTextNodes(formElement, /\bPRODUCT\b/g, productTitle);
          this.replaceDeclineButtonCategory(formElement, categoryLabel);
          this.replaceKlaviyoLeftImage(formElement, productTitle);
        });
      }

      getKlaviyoSoldOutFormId() {
        const productInfo = this.closest('product-info');
        const configuredId = productInfo?.dataset?.klaviyoSoldOutFormId?.trim();
        return configuredId || 'XWhgAM';
      }

      getCurrentProductId() {
        const productInfo = this.closest('product-info');
        return productInfo?.dataset?.productId?.toString() || '';
      }

      getSoldOutPopupStorageKey() {
        return 'klaviyo_sold_out_popup_last_seen_by_product_v1';
      }

      getSoldOutPopupThrottleMs() {
        return 24 * 60 * 60 * 1000;
      }

      getSoldOutPopupSeenMap() {
        try {
          const serialized = window.localStorage.getItem(this.getSoldOutPopupStorageKey());
          if (!serialized) return {};
          const parsed = JSON.parse(serialized);
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (error) {
          console.warn('[Klaviyo Sold Out] Failed reading popup throttle storage', error);
          return {};
        }
      }

      setSoldOutPopupSeenMap(nextMap) {
        try {
          window.localStorage.setItem(this.getSoldOutPopupStorageKey(), JSON.stringify(nextMap));
        } catch (error) {
          console.warn('[Klaviyo Sold Out] Failed writing popup throttle storage', error);
        }
      }

      shouldShowSoldOutPopupForProduct(productId) {
        if (!productId) {
          // Fail-open: if product id is missing, don't block popup.
          return true;
        }

        const now = Date.now();
        const throttleMs = this.getSoldOutPopupThrottleMs();
        const seenByProduct = this.getSoldOutPopupSeenMap();
        const lastSeenAt = Number(seenByProduct[productId] || 0);

        if (!lastSeenAt || Number.isNaN(lastSeenAt)) {
          return true;
        }

        return now - lastSeenAt >= throttleMs;
      }

      markSoldOutPopupSeen(productId) {
        if (!productId) return;

        const now = Date.now();
        const throttleMs = this.getSoldOutPopupThrottleMs();
        const maxAgeMs = throttleMs * 7;
        const seenByProduct = this.getSoldOutPopupSeenMap();

        Object.keys(seenByProduct).forEach((key) => {
          const ts = Number(seenByProduct[key] || 0);
          if (!ts || Number.isNaN(ts) || now - ts > maxAgeMs) {
            delete seenByProduct[key];
          }
        });

        seenByProduct[productId] = now;
        this.setSoldOutPopupSeenMap(seenByProduct);
      }

      waitForKlaviyoForm(formId, callback) {
        const formSelector = `form[data-testid="klaviyo-form-${formId}"]`;
        const existingForm = document.querySelector(formSelector);
        if (existingForm) {
          callback(existingForm);
          return;
        }

        const observer = new MutationObserver(() => {
          const popupForm = document.querySelector(formSelector);
          if (!popupForm) return;
          observer.disconnect();
          callback(popupForm);
        });

        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 5000);
      }

      getCurrentProductTitle() {
        const productInfo = this.closest('product-info');
        const productJsonElement = productInfo?.querySelector('[data-product-json]');
        if (!productJsonElement) return 'This product';

        try {
          const parsed = JSON.parse(productJsonElement.textContent);
          return parsed?.title || 'This product';
        } catch (error) {
          console.error('[Klaviyo Sold Out] Failed to parse product title:', error);
          return 'This product';
        }
      }

      getLastBreadcrumbLabel() {
        const breadcrumbItems = document.querySelectorAll('.breadcrumbs-nav .inline-list li');
        if (!breadcrumbItems?.length) return '';

        const lastItem = breadcrumbItems[breadcrumbItems.length - 1];
        return lastItem?.textContent?.trim() || '';
      }

      replaceTextNodes(root, regex, replacement) {
        if (!root || !regex || !replacement) return;
        const textNodeWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let currentNode = textNodeWalker.nextNode();

        while (currentNode) {
          const updatedValue = currentNode.nodeValue.replace(regex, replacement);
          if (updatedValue !== currentNode.nodeValue) {
            currentNode.nodeValue = updatedValue;
          }
          currentNode = textNodeWalker.nextNode();
        }
      }

      replaceDeclineButtonCategory(formElement, categoryLabel) {
        const declineButton = Array.from(formElement.querySelectorAll('button')).find((button) =>
          /no thanks/i.test(button.textContent || '')
        );

        if (!declineButton) return;
        declineButton.textContent = declineButton.textContent.replace(/\bcategory\b/gi, categoryLabel);
      }

      replaceKlaviyoLeftImage(formElement, productTitle) {
        const featuredImage = this.getCurrentProductFeaturedImage();
        if (!featuredImage?.src) return;

        const formImages = formElement.querySelectorAll('img');
        const leftImage = formImages?.[0];
        if (!leftImage) return;

        const imageParent = leftImage.parentElement;
        if (imageParent) {
          imageParent.style.display = 'flex';
          imageParent.style.padding = '20px';
        }

        leftImage.src = featuredImage.src;
        leftImage.removeAttribute('srcset');
        leftImage.alt = featuredImage.alt || productTitle || '';
      }

      getCurrentProductFeaturedImage() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return null;

        const activeImage =
          productInfo.querySelector('media-gallery li.is-active img') ||
          productInfo.querySelector('media-gallery .product__media-item.is-active img');
        const fallbackImage =
          productInfo.querySelector('media-gallery img') ||
          productInfo.querySelector('.product__media-wrapper img');
        const image = activeImage || fallbackImage;

        if (!image) return null;
        return {
          src: image.currentSrc || image.src || '',
          alt: image.alt || '',
        };
      }

      /**
       * Update buy button state for OTP (One Time Purchase) products
       * Preserves pre-order button text when appropriate
       */
      async updateBuyButtonForOTP() {
        try {
          // Get current cart data
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          const productInfo = this.closest('product-info');
          const currentProductId = productInfo?.dataset.productId;
          const freeCollectionId = productInfo?.dataset.freeCollectionId;
          
          if (!currentProductId || !freeCollectionId) return;

          // Count total quantity of products from the free collection in cart
          let totalCollectionQuantity = 0;
          const freeCollectionIdNum = parseInt(freeCollectionId);

          // Check if products from this free collection are in cart
          cart.items.forEach(item => {
            if (window.collectionProductIds && window.collectionProductIds.includes(item.product_id)) {
              totalCollectionQuantity += item.quantity;
            }
          });

          // Update button state based on collection limit (1 product max)
          const isAtLimit = totalCollectionQuantity >= 1;
          
          if (isAtLimit) {
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButtonText.textContent = 'Already in cart';
            this.submitButton.classList.add('disabled');
            // Update tracked state
            this.currentButtonState = {
              isPreorder: false,
              variantId: this.variantIdInput?.value || null,
              buttonText: 'Already in cart'
            };
          } else {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.classList.remove('disabled');
            
            // Check pre-order status before setting button text
            const variantId = this.variantIdInput?.value;
            if (variantId) {
              const cartItem = cart.items?.find(item => item.variant_id === parseInt(variantId));
              const cartQuantity = cartItem?.quantity || 0;
              
              // Fetch variant data (will use embedded JSON if stock is sufficient)
              const variantData = await this.fetchVariantInventory(variantId, cartQuantity) || this.getSelectedVariantData();
              
              if (variantData) {
                // Check if all stock is in cart or variant is out of stock
                const isAllStockInCart = variantData.inventory_management === 'shopify' &&
                                        variantData.inventory_quantity <= cartQuantity &&
                                        variantData.inventory_policy === 'continue' &&
                                        cartQuantity > 0;
                const isPreorder = variantData.inventory_quantity <= 0 && 
                                  variantData.inventory_policy === 'continue';
                const shouldBePreorder = isAllStockInCart || isPreorder;
                
                // Only update button text if pre-order state changed
                if (this.currentButtonState.variantId !== variantId || 
                    this.currentButtonState.isPreorder !== shouldBePreorder) {
                  
                  if (shouldBePreorder) {
                    // Use pre-order button text
                    this.submitButtonText.textContent = this.dataset.preorderButtonText || 'Pre-order now';
                    this.currentButtonState = {
                      isPreorder: true,
                      variantId: variantId,
                      buttonText: this.dataset.preorderButtonText || 'Pre-order now'
                    };
                  } else {
                    // Use regular add to cart text
                    this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
                    this.currentButtonState = {
                      isPreorder: false,
                      variantId: variantId,
                      buttonText: window.variantStrings?.addToCart || 'Add to cart'
                    };
                  }
                }
                // If state hasn't changed, preserve current button text (don't override)
              } else {
                // Fallback: only set if we don't have a tracked state
                if (!this.currentButtonState.buttonText) {
                  this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
                }
              }
            } else {
              // No variant ID: only set if we don't have a tracked state
              if (!this.currentButtonState.buttonText) {
                this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
              }
            }
          }
        } catch (error) {
          console.error('[ProductForm] Error updating buy button for OTP:', error);
        }
      }

      /**
       * Validate collection limits before allowing cart submission
       */
      async validateCollectionLimits() {
        const productInfo = this.closest('product-info');
        
        // Only validate for products with default OTP (free collection products)
        if (!productInfo || productInfo.dataset.defaultOtp !== 'true') {
          return true; // Allow normal products
        }

        try {
          // Get current cart data
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          // Count total quantity from the free collection
          let totalCollectionQuantity = 0;
          cart.items.forEach(item => {
            if (window.collectionProductIds && window.collectionProductIds.includes(item.product_id)) {
              totalCollectionQuantity += item.quantity;
            }
          });

          // Check if adding this product would exceed the limit
          if (totalCollectionQuantity >= 1) {
            this.handleErrorMessage('Only one product from this collection can be added to cart.');
            return false;
          }

          return true;
        } catch (error) {
          console.error('[ProductForm] Error validating collection limits:', error);
          return true; // Allow submission if validation fails
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check collection limits before allowing submission
        if (!this.validateCollectionLimits()) {
          return;
        }

        // Show loading state immediately
        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Check variant inventory before submission and add preorder attributes if needed
        // Do this in parallel with form preparation to minimize delay
        await this.checkAndSetPreorderAttributes();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
        
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
            
            // Apply promo discount if available after successful cart add
            this.checkAndApplyPromoDiscount();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

          });
      }

      /**
       * Check for promo cookie and apply discount if available
       */
      async checkAndApplyPromoDiscount() {
        const promoData = this.getPromoData();
        
        if (!promoData) {
          return; // No promo data found
        }

        if (promoData.applied) {
          return; // Discount already applied
        }

        // Get discount code for this source
        const discountCode = this.getDiscountCodeForSource(promoData.source);
        console.log('[ProductForm] Discount code for source:', discountCode);
        try {
          // Apply discount code directly to cart
          await this.applyDiscountCodeToCart(discountCode);
          
          // Add to cart attributes for tracking
          await this.addDiscountToCart(discountCode, promoData.source);
          
          // Mark as applied in cookie
          promoData.applied = true;
          promoData.discountCode = discountCode;
          this.setPromoCookie('promo_source', JSON.stringify(promoData), 30);
          
          console.log('[ProductForm] Promo discount applied successfully:', discountCode);
          
          // Trigger cart refresh
          this.refreshCartDisplay();
          
        } catch (error) {
          console.error('[ProductForm] Error applying promo discount:', error);
        }
      }

      /**
       * Get promo data from cookie
       */
      getPromoData() {
        const cookieValue = this.getCookie('promo_source');
        
        if (cookieValue) {
          try {
            return JSON.parse(cookieValue);
          } catch (e) {
            console.error('[ProductForm] Error parsing promo cookie:', e);
            return null;
          }
        }
        
        return null;
      }

      /**
       * Get discount code for specific source
       */
      getDiscountCodeForSource(source) {
        // Default discount codes for different sources
        const sourceDiscounts = {
          'FacebookAds': 'FBFREE',
          'GoogleAds': 'GOOGLEFREE',
          'InstagramAds': 'IGFREE',
          'promocode': 'FREE 1PK'
        };
        
        return sourceDiscounts[source] || 'FREEOFFER';
      }

      /**
       * Apply discount code directly to cart using Shopify Cart API
       */
      async applyDiscountCodeToCart(discountCode) {
        console.log(`[ProductForm] Applying discount code to cart: ${discountCode}`);

        try {
          // Get current cart
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          // Check if discount is already applied
          const currentDiscounts = cart.discount_codes || [];
          if (currentDiscounts.includes(discountCode)) {
            console.log('[ProductForm] Discount already applied to cart');
            return;
          }

          // Add discount code to existing ones

          // Update cart with discount codes
          const updateResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              discount: discountCode
            })
          });

          if (!updateResponse.ok) {
            throw new Error('Failed to apply discount code to cart');
          }

          const updatedCart = await updateResponse.json();
          console.log('[ProductForm] Discount code applied to cart successfully', updatedCart);

          // Dispatch custom event for cart updates
          document.dispatchEvent(new CustomEvent('cart:discountApplied', {
            detail: {
              discountCode: discountCode,
              cart: updatedCart
            }
          }));

        } catch (error) {
          console.error('[ProductForm] Error applying discount to cart:', error);
          throw error;
        }
      }

      /**
       * Add discount to cart via cart attributes for tracking
       */
      async addDiscountToCart(discountCode, source) {
        const cartData = {
          attributes: {
            'Discount Code': discountCode,
            'Promo Source': source || 'unknown'
          }
        };

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(cartData)
        });

        if (!response.ok) {
          throw new Error('Failed to update cart attributes');
        }

        console.log('[ProductForm] Cart attributes updated with discount code');
      }

      /**
       * Refresh cart display to show applied discounts
       */
      async refreshCartDisplay() {
        try {
          // Trigger cart update event for other components
          document.dispatchEvent(new CustomEvent('cart:updated'));
          
          // Get updated cart data
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          
          // Trigger the proper cart update event using the pub/sub system
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form-discount',
            cartData: cartData
          });
          
          console.log('[ProductForm] Cart refreshed with updated discount');
        } catch (error) {
          console.error('[ProductForm] Error refreshing cart display:', error);
        }
      }

      /**
       * Cookie helper methods
       */
      getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        
        for (let i = 0; i < ca.length; i++) {
          let c = ca[i];
          while (c.charAt(0) === ' ') c = c.substring(1, c.length);
          if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        
        return null;
      }

      setPromoCookie(name, value, days) {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
